import type { ReactElement } from "react"
import { useRef } from "react"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import { Accordion, AccordionItem, Text } from "@ionos-web-design-system/react"
import FaqPageSchema from "~/components/FaqPageSchema"
import type { Faq } from "~/fragments/faq"
import { sendFaqShowEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"

export type FaqContentProps = {
  faqs?: Faq[]
  hasFaqSchema?: boolean
}

export default function FaqContent({
  faqs,
  hasFaqSchema,
}: FaqContentProps): ReactElement<FaqContentProps> | null {
  const defaultOpensValues: string[] =
    faqs?.flatMap((faq) => (faq.flatData.isOpen ? [faq.id] : [])) ?? []

  const prevValuesRef = useRef<string[]>(defaultOpensValues)

  if (isEmpty(faqs)) {
    return null
  }

  const handleValueChange = (values: string[]) => {
    values
      .filter((value) => !prevValuesRef.current.includes(value))
      .forEach((value) => {
        const faq = faqs.find((faq) => faq.id === value)

        if (!isEmpty(faq)) {
          sendFaqShowEvent(faq.flatData.title)
          window.jentisDataLayer?.pushFaqShowEvent(faq.flatData.title)
        }
      })

    prevValuesRef.current = values
  }

  return (
    <>
      {hasFaqSchema ? (
        <FaqPageSchema
          faqContent={faqs.map((faq) => ({
            title: faq.flatData.title,
            text: <MarkdownText>{faq.flatData.text}</MarkdownText>,
            textHtml: faq.data ? faq.data.text.iv : undefined,
          }))}
        />
      ) : null}

      <Accordion
        type="multiple"
        defaultValue={defaultOpensValues}
        className="flex flex-col gap-gap-8"
        onValueChange={handleValueChange}
      >
        {faqs.map((faq) => (
          <AccordionItem
            key={faq.id}
            value={faq.id}
            trigger={
              <Text variant="headingXl" weight="bold" alignment="left">
                {faq.flatData.title}
              </Text>
            }
            className="bg-surface-base"
            data-test-id="faq-row"
          >
            <MarkdownText>{faq.flatData.text}</MarkdownText>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  )
}
